/**
 * Role-Based Access Control (RBAC) System
 * Centralized permission management for MindWell application
 */

// Define all available roles
export const ROLES = {
    DOCTOR: 'doctor',
    PATIENT: 'patient',
    RECEPTIONIST: 'receptionist',
    PHARMACIST: 'pharmacist',
    CAREGIVER: 'caregiver',
    ADMIN: 'admin'
};

// Define permission levels
export const PERMISSION_LEVELS = {
    NONE: 0,
    READ: 1,
    WRITE: 2,
    ADMIN: 3
};

// Define all available permissions
export const PERMISSIONS = {
    // Patient Management
    VIEW_PATIENTS: 'view_patients',
    CREATE_PATIENTS: 'create_patients',
    EDIT_PATIENTS: 'edit_patients',
    DELETE_PATIENTS: 'delete_patients',

    // Appointment Management
    VIEW_APPOINTMENTS: 'view_appointments',
    CREATE_APPOINTMENTS: 'create_appointments',
    EDIT_APPOINTMENTS: 'edit_appointments',
    CANCEL_APPOINTMENTS: 'cancel_appointments',

    // Doctor Management
    VIEW_DOCTORS: 'view_doctors',
    CREATE_DOCTORS: 'create_doctors',
    EDIT_DOCTORS: 'edit_doctors',
    DELETE_DOCTORS: 'delete_doctors',

    // Prescription Management
    VIEW_PRESCRIPTIONS: 'view_prescriptions',
    CREATE_PRESCRIPTIONS: 'create_prescriptions',
    EDIT_PRESCRIPTIONS: 'edit_prescriptions',
    DISPENSE_PRESCRIPTIONS: 'dispense_prescriptions',

    // Billing Management
    VIEW_BILLING: 'view_billing',
    CREATE_BILLING: 'create_billing',
    EDIT_BILLING: 'edit_billing',
    PROCESS_PAYMENTS: 'process_payments',

    // Clinic Management
    VIEW_CLINICS: 'view_clinics',
    MANAGE_CLINICS: 'manage_clinics',

    // Notifications
    SEND_NOTIFICATIONS: 'send_notifications',
    VIEW_NOTIFICATIONS: 'view_notifications',

    // Reports
    VIEW_REPORTS: 'view_reports',
    EXPORT_REPORTS: 'export_reports',

    // Admin Functions
    MANAGE_USERS: 'manage_users',
    SYSTEM_SETTINGS: 'system_settings',
    AUDIT_LOGS: 'audit_logs'
};

// Role-based permission matrix
export const ROLE_PERMISSIONS = {
    [ROLES.DOCTOR]: {
        [PERMISSIONS.VIEW_PATIENTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.EDIT_PATIENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_APPOINTMENTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.CREATE_APPOINTMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.EDIT_APPOINTMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.CANCEL_APPOINTMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_PRESCRIPTIONS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.CREATE_PRESCRIPTIONS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.EDIT_PRESCRIPTIONS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_BILLING]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_CLINICS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.SEND_NOTIFICATIONS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_NOTIFICATIONS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_REPORTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.EXPORT_REPORTS]: PERMISSION_LEVELS.READ
    },

    [ROLES.PATIENT]: {
        [PERMISSIONS.VIEW_APPOINTMENTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.CREATE_APPOINTMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.CANCEL_APPOINTMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_PRESCRIPTIONS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_BILLING]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_NOTIFICATIONS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_REPORTS]: PERMISSION_LEVELS.READ
    },

    [ROLES.RECEPTIONIST]: {
        [PERMISSIONS.VIEW_PATIENTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.CREATE_PATIENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.EDIT_PATIENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_APPOINTMENTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.CREATE_APPOINTMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.EDIT_APPOINTMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.CANCEL_APPOINTMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_DOCTORS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_BILLING]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.CREATE_BILLING]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.EDIT_BILLING]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.PROCESS_PAYMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_CLINICS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.SEND_NOTIFICATIONS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_NOTIFICATIONS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_REPORTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.EXPORT_REPORTS]: PERMISSION_LEVELS.READ
    },

    [ROLES.PHARMACIST]: {
        [PERMISSIONS.VIEW_PATIENTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_PRESCRIPTIONS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.DISPENSE_PRESCRIPTIONS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_BILLING]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.PROCESS_PAYMENTS]: PERMISSION_LEVELS.WRITE,
        [PERMISSIONS.VIEW_NOTIFICATIONS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_REPORTS]: PERMISSION_LEVELS.READ
    },

    [ROLES.CAREGIVER]: {
        [PERMISSIONS.VIEW_PATIENTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_APPOINTMENTS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_PRESCRIPTIONS]: PERMISSION_LEVELS.READ,
        [PERMISSIONS.VIEW_NOTIFICATIONS]: PERMISSION_LEVELS.READ
    },

    [ROLES.ADMIN]: {
        // Admin has all permissions
        ...Object.values(PERMISSIONS).reduce((acc, permission) => {
            acc[permission] = PERMISSION_LEVELS.ADMIN;
            return acc;
        }, {})
    }
};

// Route-based access control
export const ROUTE_ACCESS = {
    // Patient routes
    '/patient-dashboard': [ROLES.PATIENT, ROLES.CAREGIVER],
    '/patient-profile': [ROLES.PATIENT],
    '/patient-appointments': [ROLES.PATIENT, ROLES.CAREGIVER],
    '/patient-prescriptions': [ROLES.PATIENT, ROLES.CAREGIVER],
    '/patient-billing': [ROLES.PATIENT],

    // Doctor routes
    '/doctor-dashboard': [ROLES.DOCTOR],
    '/doctor-appointments': [ROLES.DOCTOR],
    '/doctor-patients': [ROLES.DOCTOR],
    '/doctor-prescriptions': [ROLES.DOCTOR],
    '/doctor-reports': [ROLES.DOCTOR],

    // Receptionist routes
    '/receptionist-dashboard': [ROLES.RECEPTIONIST],
    '/receptionist-checkin': [ROLES.RECEPTIONIST],
    '/receptionist-appointments': [ROLES.RECEPTIONIST],
    '/receptionist-patients': [ROLES.RECEPTIONIST],
    '/receptionist-billing': [ROLES.RECEPTIONIST],

    // Pharmacist routes
    '/pharmacist-dashboard': [ROLES.PHARMACIST],
    '/pharmacist-prescriptions': [ROLES.PHARMACIST],
    '/pharmacist-inventory': [ROLES.PHARMACIST],

    // Admin routes
    '/admin-dashboard': [ROLES.ADMIN],
    '/admin-users': [ROLES.ADMIN],
    '/admin-settings': [ROLES.ADMIN],
    '/admin-reports': [ROLES.ADMIN]
};

// Feature-based access control
export const FEATURE_ACCESS = {
    // Dashboard features
    'appointment-calendar': [ROLES.DOCTOR, ROLES.RECEPTIONIST],
    'patient-management': [ROLES.DOCTOR, ROLES.RECEPTIONIST],
    'prescription-management': [ROLES.DOCTOR, ROLES.PHARMACIST],
    'billing-management': [ROLES.RECEPTIONIST, ROLES.PHARMACIST],
    'report-generation': [ROLES.DOCTOR, ROLES.RECEPTIONIST, ROLES.ADMIN],

    // Patient features
    'book-appointment': [ROLES.PATIENT, ROLES.CAREGIVER],
    'view-prescriptions': [ROLES.PATIENT, ROLES.CAREGIVER],
    'payment-history': [ROLES.PATIENT],

    // Admin features
    'user-management': [ROLES.ADMIN],
    'system-settings': [ROLES.ADMIN],
    'audit-logs': [ROLES.ADMIN]
};

/**
 * Check if user has permission for a specific action
 * @param {Object} user - User object with role property
 * @param {string} permission - Permission to check
 * @param {number} requiredLevel - Required permission level (default: READ)
 * @returns {boolean} - Whether user has permission
 */
export const hasPermission = (user, permission, requiredLevel = PERMISSION_LEVELS.READ) => {
    if (!user || !user.role) return false;

    const rolePermissions = ROLE_PERMISSIONS[user.role];
    if (!rolePermissions) return false;

    const userPermissionLevel = rolePermissions[permission] || PERMISSION_LEVELS.NONE;
    return userPermissionLevel >= requiredLevel;
};

/**
 * Check if user can access a specific route
 * @param {Object} user - User object with role property
 * @param {string} route - Route to check
 * @returns {boolean} - Whether user can access route
 */
export const canAccessRoute = (user, route) => {
    if (!user || !user.role) return false;

    const allowedRoles = ROUTE_ACCESS[route];
    if (!allowedRoles) return true; // Public route

    return allowedRoles.includes(user.role);
};

/**
 * Check if user can access a specific feature
 * @param {Object} user - User object with role property
 * @param {string} feature - Feature to check
 * @returns {boolean} - Whether user can access feature
 */
export const canAccessFeature = (user, feature) => {
    if (!user || !user.role) return false;

    const allowedRoles = FEATURE_ACCESS[feature];
    if (!allowedRoles) return false; // Feature not defined

    return allowedRoles.includes(user.role);
};

/**
 * Get user's accessible routes based on role
 * @param {Object} user - User object with role property
 * @returns {Array} - Array of accessible routes
 */
export const getAccessibleRoutes = (user) => {
    if (!user || !user.role) return [];

    return Object.entries(ROUTE_ACCESS)
        .filter(([route, allowedRoles]) => allowedRoles.includes(user.role))
        .map(([route]) => route);
};

/**
 * Get user's accessible features based on role
 * @param {Object} user - User object with role property
 * @returns {Array} - Array of accessible features
 */
export const getAccessibleFeatures = (user) => {
    if (!user || !user.role) return [];

    return Object.entries(FEATURE_ACCESS)
        .filter(([feature, allowedRoles]) => allowedRoles.includes(user.role))
        .map(([feature]) => feature);
};

/**
 * Get user's permissions for a specific role
 * @param {string} role - User role
 * @returns {Object} - Object with permissions and their levels
 */
export const getRolePermissions = (role) => {
    return ROLE_PERMISSIONS[role] || {};
};

/**
 * Check if user is admin
 * @param {Object} user - User object with role property
 * @returns {boolean} - Whether user is admin
 */
export const isAdmin = (user) => {
    return user && user.role === ROLES.ADMIN;
};

/**
 * Check if user is staff (doctor, receptionist, pharmacist)
 * @param {Object} user - User object with role property
 * @returns {boolean} - Whether user is staff
 */
export const isStaff = (user) => {
    if (!user || !user.role) return false;
    return [ROLES.DOCTOR, ROLES.RECEPTIONIST, ROLES.PHARMACIST].includes(user.role);
};

/**
 * Check if user is patient or caregiver
 * @param {Object} user - User object with role property
 * @returns {boolean} - Whether user is patient or caregiver
 */
export const isPatient = (user) => {
    if (!user || !user.role) return false;
    return [ROLES.PATIENT, ROLES.CAREGIVER].includes(user.role);
};

/**
 * Get user's dashboard route based on role
 * @param {Object} user - User object with role property
 * @returns {string} - Dashboard route for the user
 */
export const getDashboardRoute = (user) => {
    if (!user || !user.role) return '/login';

    const dashboardRoutes = {
        [ROLES.DOCTOR]: '/doctor-dashboard',
        [ROLES.PATIENT]: '/patient-dashboard',
        [ROLES.RECEPTIONIST]: '/receptionist-dashboard',
        [ROLES.PHARMACIST]: '/pharmacist-dashboard',
        [ROLES.CAREGIVER]: '/patient-dashboard',
        [ROLES.ADMIN]: '/admin-dashboard'
    };

    return dashboardRoutes[user.role] || '/login';
};

/**
 * Validate user access for a specific action
 * @param {Object} user - User object with role property
 * @param {string} action - Action to validate
 * @param {Object} resource - Resource being accessed (optional)
 * @returns {Object} - Validation result with success and message
 */
export const validateAccess = (user, action, resource = null) => {
    if (!user || !user.role) {
        return {
            success: false,
            message: 'User not authenticated',
            code: 'UNAUTHENTICATED'
        };
    }

    // Check if user has permission for the action
    if (!hasPermission(user, action)) {
        return {
            success: false,
            message: `Access denied: ${action} permission required`,
            code: 'INSUFFICIENT_PERMISSIONS'
        };
    }

    // Additional resource-specific validations can be added here
    if (resource) {
        // Example: Check if user can access specific patient data
        if (action === PERMISSIONS.VIEW_PATIENTS && resource.patient_id) {
            // Add patient-specific access logic here
        }
    }

    return {
        success: true,
        message: 'Access granted',
        code: 'SUCCESS'
    };
};